/**
 * Typisierte REST-Aufrufe laut docs/API.md. Nur dünne Hüllen um `api()` – keine Logik.
 * Gruppiert nach Vertragsabschnitten.
 */
import type {
  AttachmentDto,
  AuthResponseDto,
  BanDto,
  ChannelDto,
  ChannelType,
  CurrentUserDto,
  CursorPage,
  EmbedDto,
  EmojiDto,
  ForumTagDto,
  InviteDto,
  MemberDto,
  MessageDto,
  NotificationDto,
  NotificationLevel,
  NotificationSettingDto,
  PermissionOverwriteDto,
  PresenceDto,
  PresenceStatus,
  PushSubscriptionInput,
  ReadStateDto,
  RoleDto,
  ServerDto,
  ServerFullDto,
  SessionDto,
  SoundDto,
  StickerDto,
  UserDto,
  UserSettingsDto,
  UserThemeDto,
  CustomStatusDto,
  OverwriteTarget,
} from '@lagerfeuer/shared';
import { api, apiUpload, uploadFile, type UploadOptions } from './api';

const enc = encodeURIComponent;

export interface AuthConfig {
  registrationOpen: boolean;
  discordLogin: boolean;
  vapidPublicKey: string | null;
  maxUploadBytes: number;
  maxMessageLength: number;
}

export interface UserProfileDto {
  user: UserDto;
  member: MemberDto | null;
  mutualServerIds: string[];
  mutualDmChannelId: string | null;
}

export const authApi = {
  config: () => api<AuthConfig>('/auth/config'),
  register: (body: { username: string; password: string; email?: string; displayName?: string }) =>
    api<AuthResponseDto>('/auth/register', { method: 'POST', json: body }),
  login: (body: { login: string; password: string }) => api<AuthResponseDto>('/auth/login', { method: 'POST', json: body }),
  logout: () => api<void>('/auth/logout', { method: 'POST' }),
  logoutAll: () => api<void>('/auth/logout-all', { method: 'POST' }),
  sessions: () => api<SessionDto[]>('/auth/sessions'),
  revokeSession: (id: string) => api<void>(`/auth/sessions/${id}`, { method: 'DELETE' }),
  changePassword: (body: { currentPassword?: string; newPassword: string }) => api<void>('/auth/password', { method: 'POST', json: body }),
  resetPassword: (body: { token: string; newPassword: string }) => api<void>('/auth/password-reset', { method: 'POST', json: body }),
  discordLinkUrl: () => api<{ url: string }>('/auth/discord/link', { method: 'POST' }),
  discordUnlink: () => api<void>('/auth/discord', { method: 'DELETE' }),
};

export type UserPatch = Partial<{
  username: string;
  displayName: string | null;
  email: string | null;
  bio: string | null;
  pronouns: string | null;
  bannerColor: string | null;
  themePrimary: string | null;
  themeAccent: string | null;
}>;

export const usersApi = {
  me: () => api<CurrentUserDto>('/users/@me'),
  updateMe: (patch: UserPatch) => api<CurrentUserDto>('/users/@me', { method: 'PATCH', json: patch }),
  setAvatar: (file: Blob) => uploadFile<CurrentUserDto>('/users/@me/avatar', file),
  removeAvatar: () => api<CurrentUserDto>('/users/@me/avatar', { method: 'DELETE' }),
  setBanner: (file: Blob) => uploadFile<CurrentUserDto>('/users/@me/banner', file),
  removeBanner: () => api<CurrentUserDto>('/users/@me/banner', { method: 'DELETE' }),
  deleteMe: (password?: string) => api<void>('/users/@me', { method: 'DELETE', json: { password } }),
  get: (id: string) => api<UserDto>(`/users/${id}`),
  profile: (id: string, serverId?: string) => api<UserProfileDto>(`/users/${id}/profile`, { query: { serverId } }),
  search: (q: string, limit = 10) => api<UserDto[]>('/users/search', { query: { q, limit } }),
  settings: () => api<UserSettingsDto>('/users/@me/settings'),
  updateSettings: (patch: Partial<Omit<UserSettingsDto, 'updatedAt'>>) =>
    api<UserSettingsDto>('/users/@me/settings', { method: 'PATCH', json: patch }),
  themes: () => api<UserThemeDto[]>('/users/@me/themes'),
  createTheme: (body: { name: string; css?: string; variables?: Record<string, string> }) =>
    api<UserThemeDto>('/users/@me/themes', { method: 'POST', json: body }),
  updateTheme: (id: string, body: { name?: string; css?: string; variables?: Record<string, string> }) =>
    api<UserThemeDto>(`/users/@me/themes/${id}`, { method: 'PATCH', json: body }),
  deleteTheme: (id: string) => api<void>(`/users/@me/themes/${id}`, { method: 'DELETE' }),
};

export type ServerPatch = Partial<{
  name: string;
  description: string | null;
  systemChannelId: string | null;
  rulesChannelId: string | null;
  afkChannelId: string | null;
  afkTimeoutSeconds: number;
  defaultNotifications: 'ALL' | 'MENTIONS';
}>;

export const serversApi = {
  list: () => api<ServerDto[]>('/servers'),
  create: (body: { name: string; description?: string }) => api<ServerFullDto>('/servers', { method: 'POST', json: body }),
  get: (id: string) => api<ServerFullDto>(`/servers/${id}`),
  update: (id: string, patch: ServerPatch) => api<ServerDto>(`/servers/${id}`, { method: 'PATCH', json: patch }),
  setIcon: (id: string, file: Blob) => uploadFile<ServerDto>(`/servers/${id}/icon`, file),
  removeIcon: (id: string) => api<ServerDto>(`/servers/${id}/icon`, { method: 'DELETE' }),
  setBanner: (id: string, file: Blob) => uploadFile<ServerDto>(`/servers/${id}/banner`, file),
  removeBanner: (id: string) => api<ServerDto>(`/servers/${id}/banner`, { method: 'DELETE' }),
  setVanity: (id: string, code: string | null) => api<{ code: string | null }>(`/servers/${id}/vanity`, { method: 'PUT', json: { code } }),
  transfer: (id: string, body: { userId: string; password?: string }) => api<ServerDto>(`/servers/${id}/transfer`, { method: 'POST', json: body }),
  remove: (id: string, password?: string) => api<void>(`/servers/${id}`, { method: 'DELETE', json: { password } }),
  ack: (id: string) => api<void>(`/servers/${id}/ack`, { method: 'POST' }),
};

export interface ChannelCreate {
  type: Extract<ChannelType, 'TEXT' | 'VOICE' | 'CATEGORY' | 'ANNOUNCEMENT' | 'FORUM'>;
  name: string;
  parentId?: string | null;
  topic?: string;
  nsfw?: boolean;
  slowmodeSeconds?: number;
  bitrate?: number;
  userLimit?: number;
  position?: number;
  permissionOverwrites?: PermissionOverwriteDto[];
}

export type ChannelPatch = Partial<{
  name: string;
  topic: string | null;
  nsfw: boolean;
  slowmodeSeconds: number;
  bitrate: number;
  userLimit: number;
  parentId: string | null;
  position: number;
  defaultReactionEmoji: string | null;
}>;

export const channelsApi = {
  list: (serverId: string) => api<ChannelDto[]>(`/servers/${serverId}/channels`),
  create: (serverId: string, body: ChannelCreate) => api<ChannelDto>(`/servers/${serverId}/channels`, { method: 'POST', json: body }),
  positions: (serverId: string, body: { id: string; position: number; parentId?: string | null; lockPermissions?: boolean }[]) =>
    api<void>(`/servers/${serverId}/channels/positions`, { method: 'PATCH', json: body }),
  get: (id: string) => api<ChannelDto>(`/channels/${id}`),
  update: (id: string, patch: ChannelPatch) => api<ChannelDto>(`/channels/${id}`, { method: 'PATCH', json: patch }),
  remove: (id: string) => api<void>(`/channels/${id}`, { method: 'DELETE' }),
  putOverwrite: (id: string, targetType: OverwriteTarget, targetId: string, body: { allow: string; deny: string }) =>
    api<void>(`/channels/${id}/permissions/${targetType}/${targetId}`, { method: 'PUT', json: body }),
  deleteOverwrite: (id: string, targetType: OverwriteTarget, targetId: string) =>
    api<void>(`/channels/${id}/permissions/${targetType}/${targetId}`, { method: 'DELETE' }),
  syncPermissions: (id: string) => api<void>(`/channels/${id}/permissions/sync`, { method: 'POST' }),
  createTag: (id: string, body: { name: string; emoji?: string | null; moderated?: boolean }) =>
    api<ForumTagDto>(`/channels/${id}/tags`, { method: 'POST', json: body }),
  updateTag: (id: string, tagId: string, body: { name?: string; emoji?: string | null; moderated?: boolean; position?: number }) =>
    api<ForumTagDto>(`/channels/${id}/tags/${tagId}`, { method: 'PATCH', json: body }),
  deleteTag: (id: string, tagId: string) => api<void>(`/channels/${id}/tags/${tagId}`, { method: 'DELETE' }),
  typing: (id: string) => api<void>(`/channels/${id}/typing`, { method: 'POST' }),
  ack: (id: string, messageId: string, manual?: boolean) =>
    api<ReadStateDto>(`/channels/${id}/ack`, { method: 'POST', json: manual ? { messageId, manual } : { messageId } }),
  ackPins: (id: string) => api<ReadStateDto>(`/channels/${id}/ack-pins`, { method: 'POST' }),
};

export type RoleInput = Partial<{
  name: string;
  color: number | null;
  hoist: boolean;
  permissions: string;
  mentionable: boolean;
  unicodeEmoji: string | null;
}>;

export const rolesApi = {
  list: (serverId: string) => api<RoleDto[]>(`/servers/${serverId}/roles`),
  create: (serverId: string, body: RoleInput & { name: string }) => api<RoleDto>(`/servers/${serverId}/roles`, { method: 'POST', json: body }),
  positions: (serverId: string, body: { id: string; position: number }[]) =>
    api<RoleDto[]>(`/servers/${serverId}/roles/positions`, { method: 'PATCH', json: body }),
  update: (serverId: string, roleId: string, body: RoleInput) =>
    api<RoleDto>(`/servers/${serverId}/roles/${roleId}`, { method: 'PATCH', json: body }),
  setIcon: (serverId: string, roleId: string, file: Blob) => uploadFile<RoleDto>(`/servers/${serverId}/roles/${roleId}/icon`, file),
  removeIcon: (serverId: string, roleId: string) => api<RoleDto>(`/servers/${serverId}/roles/${roleId}/icon`, { method: 'DELETE' }),
  remove: (serverId: string, roleId: string) => api<void>(`/servers/${serverId}/roles/${roleId}`, { method: 'DELETE' }),
};

export type MemberPatch = Partial<{
  nickname: string | null;
  roleIds: string[];
  timeoutUntil: string | null;
  serverMute: boolean;
  serverDeaf: boolean;
}>;

/** Optionaler Audit-Grund als Header (URL-kodiert, Vertrag 0.7). */
const reasonHeader = (reason?: string): HeadersInit | undefined => (reason ? { 'X-Audit-Reason': enc(reason) } : undefined);

export const membersApi = {
  list: (serverId: string, query: { after?: string; limit?: number; q?: string; roleId?: string } = {}) =>
    api<CursorPage<MemberDto>>(`/servers/${serverId}/members`, { query }),
  get: (serverId: string, userId: string) => api<MemberDto>(`/servers/${serverId}/members/${userId}`),
  updateMe: (serverId: string, body: { nickname?: string | null; serverBio?: string | null }) =>
    api<MemberDto>(`/servers/${serverId}/members/@me`, { method: 'PATCH', json: body }),
  setMyAvatar: (serverId: string, file: Blob) => uploadFile<MemberDto>(`/servers/${serverId}/members/@me/avatar`, file),
  removeMyAvatar: (serverId: string) => api<MemberDto>(`/servers/${serverId}/members/@me/avatar`, { method: 'DELETE' }),
  setMyBanner: (serverId: string, file: Blob) => uploadFile<MemberDto>(`/servers/${serverId}/members/@me/banner`, file),
  removeMyBanner: (serverId: string) => api<MemberDto>(`/servers/${serverId}/members/@me/banner`, { method: 'DELETE' }),
  leave: (serverId: string) => api<void>(`/servers/${serverId}/members/@me`, { method: 'DELETE' }),
  update: (serverId: string, userId: string, body: MemberPatch, reason?: string) =>
    api<MemberDto>(`/servers/${serverId}/members/${userId}`, { method: 'PATCH', json: body, headers: reasonHeader(reason) }),
  addRole: (serverId: string, userId: string, roleId: string) =>
    api<void>(`/servers/${serverId}/members/${userId}/roles/${roleId}`, { method: 'PUT' }),
  removeRole: (serverId: string, userId: string, roleId: string) =>
    api<void>(`/servers/${serverId}/members/${userId}/roles/${roleId}`, { method: 'DELETE' }),
  kick: (serverId: string, userId: string, reason?: string) =>
    api<void>(`/servers/${serverId}/members/${userId}`, { method: 'DELETE', headers: reasonHeader(reason) }),
  bans: (serverId: string, query: { after?: string; limit?: number } = {}) => api<BanDto[]>(`/servers/${serverId}/bans`, { query }),
  ban: (serverId: string, userId: string, body: { reason?: string; deleteMessageSeconds?: number } = {}) =>
    api<void>(`/servers/${serverId}/bans/${userId}`, { method: 'PUT', json: body, headers: reasonHeader(body.reason) }),
  unban: (serverId: string, userId: string) => api<void>(`/servers/${serverId}/bans/${userId}`, { method: 'DELETE' }),
};

export const invitesApi = {
  get: (code: string) => api<InviteDto>(`/invites/${enc(code)}`),
  accept: (code: string) => api<ServerFullDto>(`/invites/${enc(code)}`, { method: 'POST' }),
  create: (channelId: string, body: { maxUses?: number; maxAgeSeconds?: number } = {}) =>
    api<InviteDto>(`/channels/${channelId}/invites`, { method: 'POST', json: body }),
  list: (serverId: string) => api<InviteDto[]>(`/servers/${serverId}/invites`),
  remove: (code: string) => api<void>(`/invites/${enc(code)}`, { method: 'DELETE' }),
};

export const expressionsApi = {
  emojis: (serverId: string) => api<EmojiDto[]>(`/servers/${serverId}/emojis`),
  createEmoji: (serverId: string, file: Blob, name: string, opts?: UploadOptions) => {
    const form = new FormData();
    form.append('name', name);
    form.append('file', file);
    return apiUpload<EmojiDto>(`/servers/${serverId}/emojis`, form, opts);
  },
  renameEmoji: (serverId: string, emojiId: string, name: string) =>
    api<EmojiDto>(`/servers/${serverId}/emojis/${emojiId}`, { method: 'PATCH', json: { name } }),
  deleteEmoji: (serverId: string, emojiId: string) => api<void>(`/servers/${serverId}/emojis/${emojiId}`, { method: 'DELETE' }),
  emoji: (emojiId: string) => api<EmojiDto>(`/emojis/${emojiId}`),
  stickers: (serverId: string) => api<StickerDto[]>(`/servers/${serverId}/stickers`),
  createSticker: (serverId: string, file: Blob, fields: { name: string; description?: string; tags?: string }, opts?: UploadOptions) => {
    const form = new FormData();
    for (const [k, v] of Object.entries(fields)) if (v !== undefined) form.append(k, v);
    form.append('file', file);
    return apiUpload<StickerDto>(`/servers/${serverId}/stickers`, form, opts);
  },
  updateSticker: (serverId: string, stickerId: string, body: { name?: string; description?: string; tags?: string }) =>
    api<StickerDto>(`/servers/${serverId}/stickers/${stickerId}`, { method: 'PATCH', json: body }),
  deleteSticker: (serverId: string, stickerId: string) => api<void>(`/servers/${serverId}/stickers/${stickerId}`, { method: 'DELETE' }),
  sticker: (stickerId: string) => api<StickerDto>(`/stickers/${stickerId}`),
  sounds: (serverId: string) => api<SoundDto[]>(`/servers/${serverId}/sounds`),
  createSound: (serverId: string, file: Blob, fields: { name: string; emoji?: string; volume?: number }, opts?: UploadOptions) => {
    const form = new FormData();
    form.append('name', fields.name);
    if (fields.emoji) form.append('emoji', fields.emoji);
    if (fields.volume !== undefined) form.append('volume', String(fields.volume));
    form.append('file', file);
    return apiUpload<SoundDto>(`/servers/${serverId}/sounds`, form, opts);
  },
  updateSound: (serverId: string, soundId: string, body: { name?: string; emoji?: string | null; volume?: number }) =>
    api<SoundDto>(`/servers/${serverId}/sounds/${soundId}`, { method: 'PATCH', json: body }),
  deleteSound: (serverId: string, soundId: string) => api<void>(`/servers/${serverId}/sounds/${soundId}`, { method: 'DELETE' }),
};

export interface SendMessageBody {
  content?: string;
  nonce?: string;
  replyToId?: string;
  attachmentIds?: string[];
  stickerIds?: string[];
  flags?: number;
}

export const messagesApi = {
  list: (channelId: string, query: { before?: string; after?: string; around?: string; limit?: number } = {}) =>
    api<MessageDto[]>(`/channels/${channelId}/messages`, { query }),
  get: (channelId: string, messageId: string) => api<MessageDto>(`/channels/${channelId}/messages/${messageId}`),
  send: (channelId: string, body: SendMessageBody) => api<MessageDto>(`/channels/${channelId}/messages`, { method: 'POST', json: body }),
  edit: (channelId: string, messageId: string, body: { content?: string; flags?: number }) =>
    api<MessageDto>(`/channels/${channelId}/messages/${messageId}`, { method: 'PATCH', json: body }),
  remove: (channelId: string, messageId: string) => api<void>(`/channels/${channelId}/messages/${messageId}`, { method: 'DELETE' }),
  bulkDelete: (channelId: string, messageIds: string[]) =>
    api<void>(`/channels/${channelId}/messages/bulk-delete`, { method: 'POST', json: { messageIds } }),
  react: (channelId: string, messageId: string, key: string, isSuper = false) =>
    api<void>(`/channels/${channelId}/messages/${messageId}/reactions/${enc(key)}/@me`, { method: 'PUT', query: { super: isSuper ? 1 : undefined } }),
  unreact: (channelId: string, messageId: string, key: string, isSuper = false) =>
    api<void>(`/channels/${channelId}/messages/${messageId}/reactions/${enc(key)}/@me`, { method: 'DELETE', query: { super: isSuper ? 1 : undefined } }),
  removeUserReaction: (channelId: string, messageId: string, key: string, userId: string) =>
    api<void>(`/channels/${channelId}/messages/${messageId}/reactions/${enc(key)}/${userId}`, { method: 'DELETE' }),
  clearReaction: (channelId: string, messageId: string, key: string) =>
    api<void>(`/channels/${channelId}/messages/${messageId}/reactions/${enc(key)}`, { method: 'DELETE' }),
  clearReactions: (channelId: string, messageId: string) => api<void>(`/channels/${channelId}/messages/${messageId}/reactions`, { method: 'DELETE' }),
  reactors: (channelId: string, messageId: string, key: string, query: { after?: string; limit?: number; super?: boolean } = {}) =>
    api<UserDto[]>(`/channels/${channelId}/messages/${messageId}/reactions/${enc(key)}`, {
      query: { after: query.after, limit: query.limit, super: query.super ? 1 : undefined },
    }),
  pins: (channelId: string) => api<MessageDto[]>(`/channels/${channelId}/pins`),
  pin: (channelId: string, messageId: string) => api<void>(`/channels/${channelId}/pins/${messageId}`, { method: 'PUT' }),
  unpin: (channelId: string, messageId: string) => api<void>(`/channels/${channelId}/pins/${messageId}`, { method: 'DELETE' }),
  upload: (channelId: string, files: { file: File; spoiler?: boolean; description?: string }[], opts?: UploadOptions) => {
    const form = new FormData();
    files.forEach((f, i) => {
      if (f.spoiler) form.append(`spoiler[${i}]`, 'true');
      if (f.description) form.append(`description[${i}]`, f.description);
    });
    for (const f of files) form.append('files', f.file, f.file.name);
    return apiUpload<AttachmentDto[]>(`/channels/${channelId}/attachments`, form, opts);
  },
  deleteAttachment: (attachmentId: string) => api<void>(`/attachments/${attachmentId}`, { method: 'DELETE' }),
  embedPreview: (url: string) => api<EmbedDto>('/embeds/preview', { query: { url } }),
};

export type AutoArchive = 60 | 1440 | 4320 | 10080;

export const threadsApi = {
  fromMessage: (channelId: string, messageId: string, body: { name: string; autoArchiveMinutes?: AutoArchive }) =>
    api<ChannelDto>(`/channels/${channelId}/messages/${messageId}/threads`, { method: 'POST', json: body }),
  create: (
    channelId: string,
    body: {
      name: string;
      type?: 'PUBLIC_THREAD' | 'PRIVATE_THREAD';
      autoArchiveMinutes?: AutoArchive;
      appliedTagIds?: string[];
      message?: { content: string; attachmentIds?: string[]; stickerIds?: string[] };
    },
  ) => api<ChannelDto & { firstMessage?: MessageDto }>(`/channels/${channelId}/threads`, { method: 'POST', json: body }),
  list: (channelId: string, query: { archived?: boolean; before?: string; limit?: number; tagId?: string; sort?: 'activity' | 'created' } = {}) =>
    api<{ threads: ChannelDto[]; hasMore: boolean }>(`/channels/${channelId}/threads`, { query }),
  update: (threadId: string, body: { name?: string; archived?: boolean; locked?: boolean; autoArchiveMinutes?: AutoArchive; appliedTagIds?: string[] }) =>
    api<ChannelDto>(`/threads/${threadId}`, { method: 'PATCH', json: body }),
  remove: (threadId: string) => api<void>(`/threads/${threadId}`, { method: 'DELETE' }),
  members: (threadId: string) => api<UserDto[]>(`/threads/${threadId}/members`),
  join: (threadId: string) => api<void>(`/threads/${threadId}/members/@me`, { method: 'PUT' }),
  leave: (threadId: string) => api<void>(`/threads/${threadId}/members/@me`, { method: 'DELETE' }),
  addMember: (threadId: string, userId: string) => api<void>(`/threads/${threadId}/members/${userId}`, { method: 'PUT' }),
  removeMember: (threadId: string, userId: string) => api<void>(`/threads/${threadId}/members/${userId}`, { method: 'DELETE' }),
};

export const dmsApi = {
  list: () => api<ChannelDto[]>('/dms'),
  open: (recipientIds: string[], name?: string) => api<ChannelDto>('/dms', { method: 'POST', json: name ? { recipientIds, name } : { recipientIds } }),
  update: (channelId: string, body: { name?: string | null }) => api<ChannelDto>(`/dms/${channelId}`, { method: 'PATCH', json: body }),
  setIcon: (channelId: string, file: Blob) => uploadFile<ChannelDto>(`/dms/${channelId}/icon`, file),
  removeIcon: (channelId: string) => api<ChannelDto>(`/dms/${channelId}/icon`, { method: 'DELETE' }),
  addRecipient: (channelId: string, userId: string) => api<void>(`/dms/${channelId}/recipients/${userId}`, { method: 'PUT' }),
  removeRecipient: (channelId: string, userId: string) => api<void>(`/dms/${channelId}/recipients/${userId}`, { method: 'DELETE' }),
  close: (channelId: string) => api<void>(`/dms/${channelId}`, { method: 'DELETE' }),
};

export const presenceApi = {
  set: (status: PresenceStatus, customStatus?: CustomStatusDto | null) =>
    api<PresenceDto>('/presence/@me', { method: 'PUT', json: customStatus === undefined ? { status } : { status, customStatus } }),
  get: (userIds: string[]) => api<PresenceDto[]>('/presence', { query: { userIds: userIds.join(',') } }),
};

export const readStateApi = {
  list: () => api<ReadStateDto[]>('/read-states'),
};

export const notificationsApi = {
  list: (query: { before?: string; limit?: number; unread?: boolean } = {}) => api<NotificationDto[]>('/notifications', { query }),
  markRead: (body: { ids?: string[]; all?: true }) => api<void>('/notifications/read', { method: 'POST', json: body }),
  remove: (id: string) => api<void>(`/notifications/${id}`, { method: 'DELETE' }),
  mentions: (query: { before?: string; limit?: number; serverId?: string; everyone?: boolean; roles?: boolean } = {}) =>
    api<MessageDto[]>('/mentions', { query }),
  settings: () => api<NotificationSettingDto[]>('/notification-settings'),
  setServer: (serverId: string, body: { level: NotificationLevel; muted: boolean; mutedUntil?: string | null; suppressEveryone: boolean; suppressRoles: boolean }) =>
    api<NotificationSettingDto>(`/notification-settings/servers/${serverId}`, { method: 'PUT', json: body }),
  setChannel: (channelId: string, body: { level: NotificationLevel; muted: boolean; mutedUntil?: string | null }) =>
    api<NotificationSettingDto>(`/notification-settings/channels/${channelId}`, { method: 'PUT', json: body }),
  resetServer: (serverId: string) => api<void>(`/notification-settings/servers/${serverId}`, { method: 'DELETE' }),
  resetChannel: (channelId: string) => api<void>(`/notification-settings/channels/${channelId}`, { method: 'DELETE' }),
};

export const pushApi = {
  vapidKey: () => api<{ key: string | null }>('/push/vapid-public-key'),
  subscribe: (sub: PushSubscriptionInput) => api<void>('/push/subscriptions', { method: 'POST', json: sub }),
  unsubscribe: (endpoint: string) => api<void>('/push/subscriptions', { method: 'DELETE', json: { endpoint } }),
  test: () => api<void>('/push/test', { method: 'POST' }),
};
